import { useCallback, useRef, useState } from 'react';
import type { HeightmapAsset } from '@/core/types';
import {
  ACCEPTED_IMAGE_TYPES,
  DEFAULT_IMPORT_OPTIONS,
  heightmapFromBlob,
  type ImageImportOptions,
} from '@/core/texture/image';
import { useDesignStore } from '@/state/store';
import { Section, Segmented, Slider, Toggle } from '../components/Controls';
import { Thumbnail } from '../components/Thumbnail';
import { createLayer } from '@/core/design/defaults';

/**
 * Image import.
 *
 * The fit mode is the important choice, not the file: `wall` spreads one
 * picture over the entire grid so each panel carries a piece of it, while
 * `panel` repeats it on every panel. Everything else here exists to stop a
 * photograph from turning into unprintable noise.
 */

const FIT_OPTIONS = [
  { value: 'wall', label: 'Whole wall', help: 'One copy stretched across every panel.' },
  { value: 'cover', label: 'Cover', help: 'Fills the wall but keeps the aspect ratio.' },
  { value: 'span', label: 'Tiled', help: 'Repeats every few panels.' },
  { value: 'panel', label: 'Per panel', help: 'A copy on each panel.' },
] as const;

type FitMode = (typeof FIT_OPTIONS)[number]['value'];

export function ImageTab() {
  const design = useDesignStore((state) => state.design);
  const applyImage = useDesignStore((state) => state.applyImage);

  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fit, setFit] = useState<FitMode>('wall');
  const [options, setOptions] = useState<ImageImportOptions>(DEFAULT_IMPORT_OPTIONS);

  const importFile = useCallback(
    async (file: File): Promise<void> => {
      setBusy(true);
      setError(null);
      try {
        const asset = await heightmapFromBlob(file, options);
        applyImage(asset, fit);
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : 'Could not read that image.');
      } finally {
        setBusy(false);
      }
    },
    [applyImage, fit, options],
  );

  const imageAssets: HeightmapAsset[] = design.assets;

  return (
    <div className="image-tab">
      <div
        className={`dropzone${dragging ? ' is-dragging' : ''}${busy ? ' is-busy' : ''}`}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          const file = event.dataTransfer.files[0];
          if (file) void importFile(file);
        }}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') inputRef.current?.click();
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_IMAGE_TYPES}
          hidden
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void importFile(file);
            event.target.value = '';
          }}
        />
        <span className="dropzone__title">{busy ? 'Reading image…' : 'Drop an image here'}</span>
        <span className="dropzone__hint">
          PNG, JPEG, WebP or GIF. Bright areas become high relief.
        </span>
      </div>

      {error ? <p className="status status--error">{error}</p> : null}

      <Section
        title="Placement"
        description="How the picture is mapped onto the wall. The first two spread one image across every panel."
      >
        <Segmented<FitMode> value={fit} options={FIT_OPTIONS} onChange={setFit} />
      </Section>

      <Section
        title="Import settings"
        description="Applied while reading the file. Re-import to change them."
      >
        <Slider
          label="Detail limit"
          value={options.maxSize ?? 1024}
          min={128}
          max={2048}
          step={128}
          unit="px"
          help="Longest edge after downsampling."
          onChange={(maxSize) => setOptions((current) => ({ ...current, maxSize }))}
        />
        <Slider
          label="Smoothing"
          value={options.smoothing ?? 0.3}
          min={0}
          max={1}
          step={0.01}
          help="Removes sensor noise that would otherwise print as tiny spikes."
          onChange={(smoothing) => setOptions((current) => ({ ...current, smoothing }))}
        />
        <Toggle
          label="Stretch levels"
          checked={options.normalize ?? true}
          help="Uses the full relief depth even when the image has little contrast."
          onChange={(normalize) => setOptions((current) => ({ ...current, normalize }))}
        />
        <Toggle
          label="Invert"
          checked={options.invert ?? false}
          help="Makes dark areas the high ones."
          onChange={(invert) => setOptions((current) => ({ ...current, invert }))}
        />
      </Section>

      {imageAssets.length > 0 ? (
        <Section title="Imported images" description="Click one to use it as the relief.">
          <div className="asset-grid">
            {imageAssets.map((asset) => (
              <button
                key={asset.id}
                type="button"
                className="asset-card"
                onClick={() => applyImage(asset, fit)}
              >
                <Thumbnail
                  layers={[
                    createLayer('heightmap', { params: { asset: asset.id, fit, tile: 'mirror' } }),
                  ]}
                  assets={imageAssets}
                  panels={1}
                  size={96}
                  alt={asset.name}
                />
                <span className="asset-card__name">{asset.name}</span>
                <span className="asset-card__meta">
                  {asset.width}×{asset.height}
                </span>
              </button>
            ))}
          </div>
        </Section>
      ) : null}
    </div>
  );
}
